import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Button, Empty, Select, Space, Tag, Tooltip } from "antd";
import { useTranslation } from "react-i18next";

import "shared/assets/styles/components/robot-monitor-wall.css";
import { useAuth } from "shared/contexts/AuthProvider";
import { useUserData } from "shared/contexts/UserDataProvider";
import { useUserOrg } from "shared/contexts/UserOrgContext";
import { usePageTitle } from "shared/contexts/PageTitleContext";
import RobotPeerVideoViewer from "features/robot-teleop/components/robot-video-viewer";
import { RobotPeerConnectionProvider } from "features/robot-teleop/contexts/RobotConnectionContext";

const SELECTION_STORAGE_KEY = "rn.monitorWall";

const getSelectionStorageKey = (username, orgId) =>
  `${SELECTION_STORAGE_KEY}.${username || "anon"}.${orgId || "none"}`;

const loadSelection = (username, orgId) => {
  try {
    const stored = localStorage.getItem(getSelectionStorageKey(username, orgId));
    const parsed = stored ? JSON.parse(stored) : null;
    return Array.isArray(parsed) ? parsed : null;
  } catch (err) {
    return null;
  }
};

const saveSelection = (username, orgId, robotIds) => {
  try {
    localStorage.setItem(
      getSelectionStorageKey(username, orgId),
      JSON.stringify(robotIds)
    );
  } catch (err) {
    // Storage can be unavailable (private mode), the page still works without it
  }
};

const getColumnCount = (tileCount) =>
  tileCount <= 1 ? 1 : Math.ceil(Math.sqrt(tileCount));

// Same rule as the monitor page: the first connection that has a camera
const getCameraPeer = (robotGroup) => {
  const peerConnections = robotGroup?.connections || [];
  return (
    peerConnections.find((peer) => peer.status === "online" && peer.has_camera) ||
    peerConnections.find((peer) => peer.has_camera) ||
    null
  );
};

const RobotMonitorWall = () => {
  const { t } = useTranslation();
  const { updatePageTitle } = usePageTitle();

  const history = useHistory();
  const location = useLocation();

  const { user } = useAuth();
  const { userDataState } = useUserData();
  const { selectedOrg } = useUserOrg();

  const username = user?.username;
  const orgId = selectedOrg?.organization?.org_id;

  const [selectedIds, setSelectedIds] = useState(null);
  const [pendingIds, setPendingIds] = useState([]);
  const [focusedId, setFocusedId] = useState(null);
  const [unmutedId, setUnmutedId] = useState(null);

  useEffect(() => {
    updatePageTitle(t("monitorWall.title"));
  }, [updatePageTitle, t]);

  const robotGroups = useMemo(
    () =>
      Object.values(userDataState.robotGroups || {})
        .filter((group) => group?.group_id)
        .sort((a, b) => String(a.group_id).localeCompare(String(b.group_id))),
    [userDataState.robotGroups]
  );

  // Initial selection: url query, then the stored one, then every online robot
  useEffect(() => {
    if (selectedIds !== null || robotGroups.length === 0) {
      return;
    }

    const knownIds = new Set(robotGroups.map((group) => group.group_id));
    const idsFromUrl = new URLSearchParams(location.search).get("robots");

    if (idsFromUrl) {
      setSelectedIds(
        idsFromUrl
          .split(",")
          .map((robotId) => robotId.trim())
          .filter((robotId) => knownIds.has(robotId))
      );
      return;
    }

    const storedIds = loadSelection(username, orgId);
    if (storedIds) {
      const validIds = storedIds.filter((robotId) => knownIds.has(robotId));
      if (validIds.length > 0) {
        setSelectedIds(validIds);
        return;
      }
    }

    setSelectedIds(
      robotGroups
        .filter((group) => group.status === "online")
        .map((group) => group.group_id)
    );
  }, [robotGroups, selectedIds, location.search, username, orgId]);

  const addPendingRobots = useCallback(() => {
    if (pendingIds.length === 0) {
      return;
    }
    setSelectedIds((prevIds) => {
      const nextIds = Array.from(new Set([...(prevIds || []), ...pendingIds]));
      saveSelection(username, orgId, nextIds);
      return nextIds;
    });
    setPendingIds([]);
  }, [pendingIds, username, orgId]);

  const removeRobot = useCallback(
    (robotId) => {
      setSelectedIds((prevIds) => {
        const nextIds = (prevIds || []).filter((id) => id !== robotId);
        saveSelection(username, orgId, nextIds);
        return nextIds;
      });
      setFocusedId((id) => (id === robotId ? null : id));
      setUnmutedId((id) => (id === robotId ? null : id));
    },
    [username, orgId]
  );

  const removeAllRobots = useCallback(() => {
    setSelectedIds([]);
    saveSelection(username, orgId, []);
    setFocusedId(null);
    setUnmutedId(null);
  }, [username, orgId]);

  const selectableGroups = useMemo(
    () =>
      robotGroups.filter(
        (group) => !(selectedIds || []).includes(group.group_id)
      ),
    [robotGroups, selectedIds]
  );

  // Tiles follow the order the robots were added, not the robot name
  const shownGroups = useMemo(() => {
    const groupsById = new Map(
      robotGroups.map((group) => [group.group_id, group])
    );
    return (selectedIds || [])
      .map((robotId) => groupsById.get(robotId))
      .filter(Boolean);
  }, [robotGroups, selectedIds]);

  const onlineCount = robotGroups.filter((group) => group.status === "online")
    .length;
  const columnCount = getColumnCount(shownGroups.length);

  return (
    <div className="monitor-wall">
      <div className="wall-bar">
        <Space.Compact className="wall-picker">
          <Select
            className="wall-select"
            mode="multiple"
            allowClear
            maxTagCount="responsive"
            value={pendingIds}
            onChange={setPendingIds}
            disabled={selectableGroups.length === 0}
            placeholder={
              selectableGroups.length > 0
                ? t("monitorWall.selectRobots")
                : t("monitorWall.noneToAdd")
            }
            options={selectableGroups.map((group) => ({
              value: group.group_id,
              label: `${group.group_id}${
                group.status === "online" ? "" : ` (${t("monitorWall.offline")})`
              }`,
            }))}
          ></Select>
          <Button
            type="primary"
            onClick={addPendingRobots}
            disabled={pendingIds.length === 0}
          >
            {t("monitorWall.add")}
          </Button>
        </Space.Compact>

        <Space>
          <span className="wall-count">
            {t("monitorWall.counts", {
              shown: shownGroups.length,
              online: onlineCount,
              total: robotGroups.length,
            })}
          </span>
          <Button onClick={removeAllRobots} disabled={shownGroups.length === 0}>
            {t("monitorWall.clearAll")}
          </Button>
        </Space>
      </div>

      {shownGroups.length === 0 ? (
        <div className="wall-empty">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <React.Fragment>
                <div>{t("monitorWall.emptyTitle")}</div>
                <small>{t("monitorWall.emptyHint")}</small>
              </React.Fragment>
            }
          ></Empty>
        </div>
      ) : (
        <div
          className={"wall-grid" + (focusedId ? " is-focused" : "")}
          style={{
            gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
          }}
        >
          {shownGroups.map((group) => {
            const robotId = group.group_id;
            const peer = getCameraPeer(group);
            const isOnline = group.status === "online";
            const isUnmuted = unmutedId === robotId;

            return (
              <div
                key={robotId}
                className={
                  "wall-tile" +
                  (isOnline ? "" : " is-offline") +
                  (focusedId === robotId ? " is-focus" : "")
                }
                onDoubleClick={() =>
                  setFocusedId((id) => (id === robotId ? null : robotId))
                }
                onClick={() =>
                  setUnmutedId((id) => (id === robotId ? null : robotId))
                }
              >
                <div className="wall-tile-label">
                  <span className="wall-tile-name">{robotId}</span>
                  {!isOnline && (
                    <Tag color="default">{t("monitorWall.offline")}</Tag>
                  )}
                  {isOnline && peer && isUnmuted && (
                    <Tag color="blue">{t("monitorWall.audioOn")}</Tag>
                  )}
                </div>

                <div className="wall-tile-video">
                  {isOnline && peer ? (
                    <RobotPeerConnectionProvider
                      StatsEnabled={false}
                      allowLocalAudio={false}
                      allowRemoteAudio={isUnmuted}
                      allowRemoteVideo={true}
                      isReceiveAudio={isUnmuted}
                      isSendingAudio={false}
                      trickleICE={true}
                      disableChannelMessageState={true}
                      dataChannels={["chat"]}
                    >
                      <RobotPeerVideoViewer
                        PeerID={peer.id}
                        ShowDelay={false}
                        ShowStatus={false}
                        ShowAlert={false}
                      ></RobotPeerVideoViewer>
                    </RobotPeerConnectionProvider>
                  ) : (
                    <div className="wall-tile-placeholder">
                      {isOnline
                        ? t("monitorWall.noCamera")
                        : t("monitorWall.disconnected")}
                    </div>
                  )}
                </div>

                <div className="wall-tile-actions">
                  <Space size={4}>
                    <Button
                      size="small"
                      onClick={(evt) => {
                        evt.stopPropagation();
                        history.push(`/remote-control/${robotId}`);
                      }}
                    >
                      {t("monitorWall.control")}
                    </Button>
                    <Tooltip title={t("monitorWall.removeTip")}>
                      <Button
                        size="small"
                        danger
                        onClick={(evt) => {
                          evt.stopPropagation();
                          removeRobot(robotId);
                        }}
                      >
                        {t("monitorWall.remove")}
                      </Button>
                    </Tooltip>
                  </Space>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RobotMonitorWall;
